import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';
import { Panel } from '../panels/panel';
import { WorkspacePanel } from '../panels/workspace-panel';
import { CommandLineEditor } from './command-line-editor';
import {
    convertCommandLineArgumentConfigurationViewsToCommandLineConfigView,
    toggleCommandLineHeight,
} from './helpers';
import { Flex, HStack, Box, IconButton } from '@cad-arc';
import { CliArgumentEnum, CliResource } from '@mark43/cad-api';
import { PanelTitle } from '../panels/panel-title';
import { observer } from 'mobx-react-lite';
import { useIsDexEnabled } from '../data-exchange/use-dex-access-guard';

const WrapCommandLineEditor = observer(function WrapCommandLineEditor({
    panel,
}: {
    panel: WorkspacePanel;
}) {
    const apiClient = useApiClient();
    const { isDexEnabled } = useIsDexEnabled();
    const { data: rawCliViews } = useQuery(['command-line-config'], () => {
        return apiClient
            .request<CliResource.GetCliArgumentConfigurationViews>({
                method: 'GET',
                url: 'cli',
                params: {
                    include_inactive: false,
                },
            })
            .then((resp) => {
                return resp.data.data;
            });
    });

    const cliViews = useMemo(() => {
        if (!isDexEnabled) {
            return rawCliViews?.filter((view) => !CliArgumentEnum[view.cliArgument]?.isDexCommand);
        }
        return rawCliViews;
    }, [rawCliViews, isDexEnabled]);

    const config = useMemo(() => {
        return cliViews
            ? convertCommandLineArgumentConfigurationViewsToCommandLineConfigView(cliViews)
            : undefined;
    }, [cliViews]);

    return (
        <>
            {config ? (
                <CommandLineEditor panel={panel} config={config} />
            ) : null}
        </>
    );
});

export const CommandLinePanel = observer(function CommandLinePanel({
    panel,
}: {
    panel: WorkspacePanel;
}) {
    const onClick = useCallback(() => {
        toggleCommandLineHeight(panel);
    }, [panel]);

    const header = (
        <Flex
            style={{ gap: '10px' }}
            alignItems="center"
            justifyContent="space-between"
            width="100%"
        >
            <PanelTitle>Command Line</PanelTitle>
            <HStack alignItems="center" flexShrink={0}>
                <Box>
                    <IconButton
                        onClick={onClick}
                        variant="ghost"
                        aria-label="expand"
                        icon="Window"
                    />
                </Box>
            </HStack>
        </Flex>
    );

    return <Panel panel={panel} header={header} body={<WrapCommandLineEditor panel={panel} />} />;
});
